import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import MovementFormDialog from "../../../components/dashboard/erp/MovementFormDialog";
import InitialStockCountDialog from "../../../components/dashboard/erp/InitialStockCountDialog";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import ContextualHelp from "../../../components/dashboard/ContextualHelp";
import ResponsiveListControls from "../../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import {
  ERP_BRAND,
  entityId,
  formatDateTime,
  formatMoney,
  projectProductCode,
  projectProductName,
  projectProductStock,
} from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";
import { useListScrollRestoration, usePersistentListValue } from "../../../utils/listViewState";

function productUnit(product = {}) {
  return String(product.unit || product.unidad || "").trim();
}

function productReorderPoint(product = {}) {
  const value = Number(product.inventory?.reorderPoint ?? product.reorderPoint ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function numericQuantity(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatQuantity(value) {
  return new Intl.NumberFormat("es-AR", { maximumFractionDigits: 3 }).format(numericQuantity(value));
}

function variantIdOf(variant = {}) {
  return String(variant?._id || variant?.id || "");
}

function productHasVariants(product = {}) {
  return Boolean(
    product.hasVariants === true
      || (Array.isArray(product.variantAttributes) && product.variantAttributes.length > 0
        && Array.isArray(product.variants) && product.variants.length > 0),
  );
}

function productVariantLabel(product = {}, variant = {}) {
  const axes = Array.isArray(product.variantAttributes) ? product.variantAttributes : [];
  const values = axes
    .map((axis) => variant?.attributes?.[axis?.key])
    .filter(Boolean);
  if (values.length) return values.join(" / ");
  const fallbackValues = Object.values(variant?.attributes || {}).filter(Boolean);
  return fallbackValues.join(" / ");
}

function pendingReceiptKey(productId, variantId = "") {
  return `${String(productId || "")}:${String(variantId || "")}`;
}

function stockStatusForRow(row = {}) {
  const stock = numericQuantity(row.availableStock);
  const reorderPoint = numericQuantity(row.reorderPoint);
  if (stock <= 0) return { key: "out", label: "Sin stock", color: "error", variant: "filled" };
  if (reorderPoint > 0 && stock <= reorderPoint) return { key: "low", label: "Reponer", color: "warning", variant: "outlined" };
  return { key: "available", label: "Disponible", color: "success", variant: "outlined" };
}


export default function ProjectErpStockPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { access, refreshBootstrap } = useOutletContext();
  const canManageStock = hasProjectPermission(access, "erp.stock.manage");
  const canReadMovements = hasProjectPermission(access, "erp.movements.read");
  const canConfirmMovements = hasProjectPermission(access, "erp.movements.confirm");
  const canReadItems = hasProjectPermission(access, "items.read") || hasProjectPermission(access, "items.update") || hasProjectPermission(access, "items.create");
  const canManageCount = canManageStock && hasProjectPermission(access, "items.read");
  const [stockMovements, setStockMovements] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [productsLoading, setProductsLoading] = React.useState(true);
  const [pendingReceipts, setPendingReceipts] = React.useState([]);
  const [unidentifiedReceipts, setUnidentifiedReceipts] = React.useState([]);
  const [pendingReceiptsLoading, setPendingReceiptsLoading] = React.useState(true);
  const [productSearch, setProductSearch] = React.useState("");
  const [inventoryFilter, setInventoryFilter] = usePersistentListValue(`erp-stock:${projectId}:inventory-filter`, "all");
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = usePersistentListValue(`erp-stock:${projectId}:page`, 0);
  const [rowsPerPage, setRowsPerPage] = usePersistentListValue(`erp-stock:${projectId}:page-size`, 25);
  const [filters, setFilters] = usePersistentListValue(`erp-stock:${projectId}:filters`, { productId: "", dateFrom: "", dateTo: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [movementAction, setMovementAction] = React.useState(null);
  const [stockCountOpen, setStockCountOpen] = React.useState(false);
  const [activeStockCount, setActiveStockCount] = React.useState(null);
  const [activeStockCountLoaded, setActiveStockCountLoaded] = React.useState(false);

  const loadProducts = React.useCallback(async () => {
    try {
      setProductsLoading(true);
      const data = await productsApi.list(projectId, { stockTracked: true });
      setProducts(data.products || []);
    } catch (err) {
      setError((current) => current || err.message || "No se pudieron cargar las existencias actuales");
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, [projectId]);

  const loadPendingReceipts = React.useCallback(async () => {
    try {
      setPendingReceiptsLoading(true);
      const data = await erpApi.listPendingStockReceipts(projectId);
      setPendingReceipts(data.pendingReceipts || []);
      setUnidentifiedReceipts(data.unidentifiedReceipts || []);
    } catch (err) {
      setError((current) => current || err.message || "No se pudo cargar la mercadería en camino");
      setPendingReceipts([]);
      setUnidentifiedReceipts([]);
    } finally {
      setPendingReceiptsLoading(false);
    }
  }, [projectId]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listStockMovements(projectId, { page: page + 1, pageSize: rowsPerPage, ...filters });
      setStockMovements(data.stockMovements || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudo cargar la trazabilidad de inventario");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  const loadActiveStockCount = React.useCallback(async () => {
    setActiveStockCountLoaded(false);
    if (!canManageCount) {
      setActiveStockCount(null);
      setActiveStockCountLoaded(true);
      return null;
    }
    try {
      const data = await erpApi.getActiveStockCount(projectId);
      const next = data.session ? { session: data.session, summary: data.summary } : null;
      setActiveStockCount(next);
      return next;
    } catch {
      // La consulta de continuación es secundaria: no debe bloquear la pantalla Stock.
      setActiveStockCount(null);
      return null;
    } finally {
      setActiveStockCountLoaded(true);
    }
  }, [canManageCount, projectId]);

  React.useEffect(() => { loadProducts(); }, [loadProducts]);
  React.useEffect(() => { loadPendingReceipts(); }, [loadPendingReceipts]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { loadActiveStockCount(); }, [loadActiveStockCount]);

  React.useEffect(() => {
    if (!activeStockCountLoaded || searchParams.get("conteo") !== "1") return;
    if (canManageCount) setStockCountOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("conteo");
    setSearchParams(nextParams, { replace: true });
  }, [activeStockCountLoaded, canManageCount, searchParams, setSearchParams]);

  React.useEffect(() => {
    if (productsLoading || searchParams.get("inventario") !== "inicial") return;
    if (canManageStock && products.length > 0) {
      setMovementAction({ kind: "opening_balance", productId: "", product: null });
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("inventario");
    setSearchParams(nextParams, { replace: true });
  }, [canManageStock, products.length, productsLoading, searchParams, setSearchParams]);

  useListScrollRestoration(`erp-stock:${projectId}`, !loading && !productsLoading && !pendingReceiptsLoading);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const inventoryRows = React.useMemo(() => {
    const pendingByKey = new Map(
      pendingReceipts.map((receipt) => [
        pendingReceiptKey(receipt.productId, receipt.variantId),
        receipt,
      ]),
    );
    const usedPendingKeys = new Set();
    const rows = [];

    products.forEach((product) => {
      const productId = entityId(product);
      const base = {
        product,
        productId,
        name: projectProductName(product),
        category: product.category || product.categoria || "",
        unit: productUnit(product),
        reorderPoint: productReorderPoint(product),
      };
      const variants = Array.isArray(product.variants) ? product.variants : [];

      if (productHasVariants(product) && variants.length) {
        variants.forEach((variant) => {
          const variantId = variantIdOf(variant);
          const key = pendingReceiptKey(productId, variantId);
          const pendingReceipt = pendingByKey.get(key) || null;
          const pendingUnits = numericQuantity(pendingReceipt?.pendingUnits);
          const availableStock = numericQuantity(variant.stock);
          if (variant.active === false && availableStock === 0 && pendingUnits <= 0) return;
          if (pendingReceipt) usedPendingKeys.add(key);

          rows.push({
            ...base,
            key: `${productId}:${variantId || "variant"}`,
            variantId,
            variantLabel: productVariantLabel(product, variant) || variant.sku || "Variante",
            code: variant.sku || projectProductCode(product),
            availableStock,
            pendingUnits,
            projectedStock: availableStock + pendingUnits,
            pendingReceipt,
            inactiveVariant: variant.active === false,
          });
        });

        const productLevelKey = pendingReceiptKey(productId, "");
        const productLevelPending = pendingByKey.get(productLevelKey) || null;
        if (productLevelPending && numericQuantity(productLevelPending.pendingUnits) > 0) {
          usedPendingKeys.add(productLevelKey);
          rows.push({
            ...base,
            key: `${productId}:pending-without-variant`,
            variantId: "",
            variantLabel: "Compra pendiente sin variante",
            code: productLevelPending.codeSnapshot || projectProductCode(product),
            availableStock: 0,
            pendingUnits: numericQuantity(productLevelPending.pendingUnits),
            projectedStock: numericQuantity(productLevelPending.pendingUnits),
            pendingReceipt: productLevelPending,
            legacyPendingWithoutVariant: true,
          });
        }
        return;
      }

      const key = pendingReceiptKey(productId, "");
      const pendingReceipt = pendingByKey.get(key) || null;
      const pendingUnits = numericQuantity(pendingReceipt?.pendingUnits);
      if (pendingReceipt) usedPendingKeys.add(key);
      const availableStock = projectProductStock(product);
      rows.push({
        ...base,
        key: productId,
        variantId: "",
        variantLabel: "",
        code: projectProductCode(product),
        availableStock,
        pendingUnits,
        projectedStock: availableStock + pendingUnits,
        pendingReceipt,
      });
    });

    pendingReceipts.forEach((receipt) => {
      const key = pendingReceiptKey(receipt.productId, receipt.variantId);
      if (usedPendingKeys.has(key) || numericQuantity(receipt.pendingUnits) <= 0) return;
      rows.push({
        key: `pending:${key}`,
        product: null,
        productId: String(receipt.productId || ""),
        variantId: String(receipt.variantId || ""),
        name: receipt.productNameSnapshot || "Ítem de compra",
        variantLabel: receipt.variantLabelSnapshot || receipt.variantSkuSnapshot || "",
        category: "",
        unit: "",
        code: receipt.variantSkuSnapshot || receipt.codeSnapshot || "",
        reorderPoint: 0,
        availableStock: 0,
        pendingUnits: numericQuantity(receipt.pendingUnits),
        projectedStock: numericQuantity(receipt.pendingUnits),
        pendingReceipt: receipt,
        missingCatalogItem: true,
      });
    });

    return rows;
  }, [pendingReceipts, products]);

  const totalPendingUnits = React.useMemo(
    () => pendingReceipts.reduce((sum, receipt) => sum + numericQuantity(receipt.pendingUnits), 0),
    [pendingReceipts],
  );
  const totalUnidentifiedUnits = React.useMemo(
    () => unidentifiedReceipts.reduce((sum, receipt) => sum + numericQuantity(receipt.quantity), 0),
    [unidentifiedReceipts],
  );

  const filteredInventoryRows = React.useMemo(() => {
    const term = String(productSearch || "").trim().toLowerCase();
    return inventoryRows.filter((row) => {
      const status = stockStatusForRow(row);
      if (inventoryFilter === "incoming" && row.pendingUnits <= 0) return false;
      if (inventoryFilter === "available" && status.key !== "available") return false;
      if (inventoryFilter === "low" && status.key !== "low") return false;
      if (inventoryFilter === "out" && status.key !== "out") return false;
      if (!term) return true;
      return [row.name, row.variantLabel, row.code, row.category, row.unit]
        .some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [inventoryFilter, inventoryRows, productSearch]);


  const openMovement = React.useCallback((kind, productId = "", product = null) => {
    if (!canManageStock) return;
    setMovementAction({ kind, productId: String(productId || ""), product });
  }, [canManageStock]);

  const refreshAfterMovement = React.useCallback(async () => {
    await Promise.all([loadProducts(), loadPendingReceipts(), load(), refreshBootstrap?.()]);
  }, [load, loadPendingReceipts, loadProducts, refreshBootstrap]);

  const actionButtons = canManageStock ? (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
      {canManageCount ? (
        <Box data-wp-tutorial-target="stock-count" sx={{ width: { xs: "100%", sm: "auto" } }}>
          <Button
            variant={activeStockCount ? "contained" : "outlined"}
            startIcon={<ChecklistRoundedIcon />}
            onClick={() => setStockCountOpen(true)}
            sx={{ minHeight: 46, width: { xs: "100%", sm: "auto" }, textTransform: "none", fontWeight: 900 }}
          >
            {activeStockCount ? "Continuar conteo" : "Conteo inicial"}
          </Button>
          {activeStockCount?.summary ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: ERP_BRAND.muted, textAlign: { xs: "left", sm: "center" } }}>
              {activeStockCount.summary.counted} de {activeStockCount.summary.total} contados · {activeStockCount.summary.pending} pendientes
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <Button
        variant="contained"
        startIcon={<AddRoundedIcon />}
        onClick={() => openMovement("stock_in")}
        sx={{ minHeight: 46, textTransform: "none", fontWeight: 900 }}
      >
        Ingresar stock
      </Button>
      <Button
        variant="outlined"
        startIcon={<RemoveRoundedIcon />}
        onClick={() => openMovement("stock_out")}
        sx={{ minHeight: 46, textTransform: "none", fontWeight: 900 }}
      >
        Descontar stock
      </Button>
      <Button
        variant="outlined"
        startIcon={<TuneRoundedIcon />}
        onClick={() => openMovement("stock_adjustment")}
        sx={{ minHeight: 46, textTransform: "none", fontWeight: 900 }}
      >
        Corregir stock
      </Button>
    </Stack>
  ) : null;

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle
        title="Stock"
        description="Separá stock físico disponible de mercadería comprada que todavía está esperando llegada."
        action={actionButtons}
      />

      {totalUnidentifiedUnits > 0 ? (
        <Alert
          severity="warning"
          action={canReadMovements && unidentifiedReceipts[0]?.movementId ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?movementId=${encodeURIComponent(String(unidentifiedReceipts[0].movementId))}`)}
              sx={{ fontWeight: 900, textTransform: "none" }}
            >
              Identificar ahora
            </Button>
          ) : null}
        >
          Hay {formatQuantity(totalUnidentifiedUnits)} unidad{totalUnidentifiedUnits === 1 ? "" : "es"} comprada{totalUnidentifiedUnits === 1 ? "" : "s"} pendiente{totalUnidentifiedUnits === 1 ? "" : "s"} de identificar. La factura está registrada, pero todavía no cuentan como stock porque falta definir producto, color o talle.
        </Alert>
      ) : null}

      <ErpPanel>
        <Box sx={{ p: { xs: 1.25, sm: 2.2 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950 }}>Existencias actuales</Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                  Disponible es stock físico vendible. En camino sólo se suma cuando recibís la compra.
                </Typography>
                {totalPendingUnits > 0 ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${formatQuantity(totalPendingUnits)} en camino`}
                    sx={{ mt: 0.75, fontWeight: 900 }}
                  />
                ) : null}
              </Box>
              <Tooltip title="Actualizar existencias">
                <span>
                  <IconButton
                    onClick={() => Promise.all([loadProducts(), loadPendingReceipts(), load()])}
                    disabled={loading || productsLoading || pendingReceiptsLoading}
                    aria-label="Actualizar stock"
                    sx={{ border: `1px solid ${ERP_BRAND.border}`, borderRadius: 2.5 }}
                  >
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                placeholder="Buscar ítem, variante, SKU o categoría"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchRoundedIcon sx={{ color: ERP_BRAND.muted }} /></InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Estado de inventario"
                value={inventoryFilter}
                onChange={(event) => setInventoryFilter(event.target.value)}
                sx={{ minWidth: { md: 230 } }}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="available">Disponibles</MenuItem>
                <MenuItem value="low">Bajo stock</MenuItem>
                <MenuItem value="out">Sin stock</MenuItem>
                <MenuItem value="incoming">Esperando llegada</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </Box>

        {productsLoading || pendingReceiptsLoading ? (
          <ErpLoading label="Cargando existencias y mercadería en camino..." />
        ) : !products.length && !pendingReceipts.length ? (
          <ErpEmpty
            title="Todavía no hay ítems con control de stock"
            description="Creá o configurá un producto o insumo que controle existencias para empezar a operar stock."
            action={canReadItems ? (
              <Button
                variant="contained"
                startIcon={<Inventory2RoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/productos`)}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Ir a Ítems
              </Button>
            ) : null}
          />
        ) : !filteredInventoryRows.length ? (
          <ErpEmpty
            title={inventoryFilter === "incoming" ? "No hay mercadería esperando llegada" : "No encontramos ese ítem"}
            description={inventoryFilter === "incoming" ? "Las compras pendientes de recepción aparecerán acá sin sumarse al stock disponible." : "Probá con otra búsqueda o cambiá el filtro de inventario."}
            action={(productSearch || inventoryFilter !== "all") ? (
              <Button
                onClick={() => { setProductSearch(""); setInventoryFilter("all"); }}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Limpiar filtros
              </Button>
            ) : null}
          />
        ) : (
          <TableContainer sx={{ p: { xs: 1.1, sm: 0 } }}>
            <ResponsiveOperationalTable
              mobileLabel="Existencias actuales"
              collapseAfter={4}
              mobileEssentialLabels={["Ítem", "Disponible", "En camino", "Estado"]}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Ítem</TableCell>
                  <TableCell align="right">Disponible</TableCell>
                  <TableCell align="right">En camino</TableCell>
                  <TableCell align="right">Proyectado</TableCell>
                  <TableCell>Unidad</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Código</TableCell>
                  {canManageStock || canReadMovements ? <TableCell align="right">Acciones</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInventoryRows.map((row) => {
                  const status = stockStatusForRow(row);
                  const receipt = row.pendingReceipt;
                  return (
                    <TableRow key={row.key} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{row.name}</Typography>
                        {row.variantLabel ? (
                          <Typography variant="caption" sx={{ display: "block", color: row.inactiveVariant ? "warning.main" : ERP_BRAND.muted, fontWeight: row.inactiveVariant ? 800 : 500 }}>
                            {row.variantLabel}{row.inactiveVariant ? " · variante inactiva" : ""}
                          </Typography>
                        ) : null}
                        {row.category ? (
                          <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted }}>{row.category}</Typography>
                        ) : null}
                        {row.missingCatalogItem ? (
                          <Typography variant="caption" sx={{ display: "block", color: "warning.main", fontWeight: 800 }}>
                            Ya no figura en el catálogo actual; se conserva por la compra pendiente.
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: 20, fontWeight: 950, color: ERP_BRAND.dark }}>{formatQuantity(row.availableStock)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.pendingUnits > 0 ? (
                          <Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 950, color: "warning.main" }}>+{formatQuantity(row.pendingUnits)}</Typography>
                            {Number(receipt?.purchaseLines || 0) > 1 ? (
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                {receipt.purchaseLines} compras pendientes
                              </Typography>
                            ) : null}
                          </Box>
                        ) : <Typography sx={{ color: ERP_BRAND.muted }}>—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>{formatQuantity(row.projectedStock)}</Typography>
                      </TableCell>
                      <TableCell>{row.unit || "—"}</TableCell>
                      <TableCell>
                        <Stack spacing={0.6} alignItems="flex-start">
                          <Chip size="small" label={status.label} color={status.color} variant={status.variant} sx={{ fontWeight: 850 }} />
                          {row.pendingUnits > 0 ? (
                            <Chip size="small" label={`${formatQuantity(row.pendingUnits)} esperando llegada`} color="warning" variant="outlined" sx={{ fontWeight: 850 }} />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.code || "—"}</TableCell>
                      {canManageStock || canReadMovements ? (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.75} justifyContent="flex-end" useFlexGap flexWrap="wrap">
                            {row.pendingUnits > 0 && canReadMovements && receipt?.latestMovementId ? (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?movementId=${encodeURIComponent(String(receipt.latestMovementId))}`)}
                                sx={{ minHeight: 40, textTransform: "none", fontWeight: 900 }}
                              >
                                Ver compra
                              </Button>
                            ) : null}
                            {canManageStock && row.product ? (
                              <>
                                <Button size="small" variant="contained" onClick={() => openMovement("stock_in", row.productId, row.product)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 850 }}>Ingresar</Button>
                                <Button size="small" variant="outlined" onClick={() => openMovement("stock_out", row.productId, row.product)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 850 }}>Descontar</Button>
                                <Button size="small" variant="text" onClick={() => openMovement("stock_adjustment", row.productId, row.product)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 850 }}>Corregir</Button>
                              </>
                            ) : null}
                          </Stack>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>
        )}
      </ErpPanel>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <ContextualHelp
          compact
          title="Cómo leer el stock"
          text="Disponible es lo que existe físicamente y puede venderse. En camino representa compras confirmadas que todavía no fueron recibidas y no modifican el stock real."
          bullets={["Proyectado = disponible + en camino; es informativo y no habilita ventas.", "Para recibir una compra pendiente, abrí su movimiento y usá “Recibir mercadería”."]}
        />
      </Box>

      <ErpPanel>
        <Box sx={{ p: { xs: 1.25, sm: 2.2 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack spacing={1.2}>
            <Box>
              <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950 }}>Historial de stock</Typography>
              <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>Movimientos confirmados para revisar qué cambió y cuándo.</Typography>
            </Box>
            <ResponsiveListControls
              search={(
                <Box sx={{ display: "flex", alignItems: "center", minHeight: 40, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: ERP_BRAND.muted, fontWeight: 800 }}>
                    {total} {total === 1 ? "movimiento" : "movimientos"}
                  </Typography>
                </Box>
              )}
              activeFilterCount={[filters.productId, filters.dateFrom, filters.dateTo].filter(Boolean).length}
              onClear={() => {
                setFilters({ productId: "", dateFrom: "", dateTo: "" });
                setPage(0);
              }}
              drawerTitle="Filtrar historial de stock"
            >
              <TextField select size="small" label="Producto" value={filters.productId} onChange={(event) => setFilter("productId", event.target.value)} sx={{ minWidth: { sm: 320 } }}>
                <MenuItem value="">Todos los productos</MenuItem>
                {products.map((product) => <MenuItem key={entityId(product)} value={entityId(product)}>{projectProductName(product)}{projectProductCode(product) ? ` · ${projectProductCode(product)}` : ""}</MenuItem>)}
              </TextField>
              <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
            </ResponsiveListControls>
          </Stack>
        </Box>
        {loading ? <ErpLoading label="Cargando movimientos de inventario..." /> : !stockMovements.length ? <ErpEmpty title="Sin movimientos de inventario" description="Las ventas, compras, entradas, salidas y ajustes confirmados aparecerán aquí." /> : (
          <>
            <TableContainer>
              <ResponsiveOperationalTable mobileLabel="Movimientos de stock" collapseAfter={4}>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Existencia anterior</TableCell>
                    <TableCell align="right">Variación</TableCell>
                    <TableCell align="right">Existencia posterior</TableCell>
                    <TableCell align="right">Costo unitario</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockMovements.map((movement) => (
                    <TableRow key={entityId(movement)} hover>
                      <TableCell>{formatDateTime(movement.occurredAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>{movement.productNameSnapshot}</Typography>
                        {movement.metadata?.recipeIngredient === true ? (
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                            Receta · {movement.metadata?.recipeParentProductName || "producto"}{movement.metadata?.sourceNumber ? ` · ${movement.metadata.sourceNumber}` : ""}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{movement.kind === "in" ? "Entrada" : movement.kind === "out" ? "Salida" : movement.kind === "adjustment" ? "Ajuste" : "Reversa"}</TableCell>
                      <TableCell align="right">{movement.stockBefore}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 950, color: Number(movement.quantityDelta) >= 0 ? "success.main" : "error.main" }}>{Number(movement.quantityDelta) > 0 ? `+${movement.quantityDelta}` : movement.quantityDelta}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>{movement.stockAfter}</TableCell>
                      <TableCell align="right">{formatMoney(movement.unitCost)}</TableCell>
                      <TableCell>{movement.createdByName || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveOperationalTable>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} onPageChange={(_event, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página" />
          </>
        )}
      </ErpPanel>

      <MovementFormDialog
        open={Boolean(movementAction)}
        onClose={() => setMovementAction(null)}
        onSaved={refreshAfterMovement}
        projectId={projectId}
        access={access}
        initialKind={movementAction?.kind || ""}
        initialProductId={movementAction?.productId || ""}
        initialProduct={movementAction?.product || null}
      />

      {canManageCount ? (
        <InitialStockCountDialog
          open={stockCountOpen}
          onClose={() => setStockCountOpen(false)}
          projectId={projectId}
          activeCount={activeStockCount}
          canConfirm={canConfirmMovements}
          onChanged={async (event) => {
            await loadActiveStockCount();
            if (event?.completed) {
              await refreshAfterMovement();
            } else {
              await refreshBootstrap?.();
            }
          }}
          onGoItems={() => {
            setStockCountOpen(false);
            navigate(`/dashboard/proyectos/${projectId}/productos`);
          }}
          onGoMovements={() => {
            setStockCountOpen(false);
            navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos`);
          }}
        />
      ) : null}
    </Stack>
  );
}
